import { c as _c } from "react/compiler-runtime";
import axios, { type AxiosInstance } from "axios";
import { useCallback } from "react";
const DEFAULT_TIMEOUT_MS = 60_000;

// One client per timeout so hooks that put `api` in useCallback deps keep a
// stable identity across re-renders (Codesmith streaming was recreating
// SessionSidebar props every tick via a fresh axios instance each call).
const clientsByTimeout = new Map<number, AxiosInstance>();
function getClient(timeoutMs: number): AxiosInstance {
  const existing = clientsByTimeout.get(timeoutMs);
  if (existing) return existing;
  const client = axios.create({
    baseURL: `${process.env.NEXT_PUBLIC_API_URL}/api`,
    timeout: timeoutMs
  });
  client.interceptors.request.use(config => {
    config.withCredentials = true;
    return config;
  }, error => {
    console.error(`Error on request: ${error}`);
    return Promise.reject(error);
  });
  client.interceptors.response.use(response => {
    return response;
  }, async error => {
    console.error(`Error on response: ${error}`);
    if (error.response?.status === 401) {
      // TODO(aayush): Rationalize how we're going to show a re-login modal to the user
      // when their session expires due to inactivity.
      console.error("Unauthorized");
    }
    return Promise.reject(error);
  });
  clientsByTimeout.set(timeoutMs, client);
  return client;
}

// useApi is a hook that returns an axios instance that is already hydrated with the
// proper base URL and authentication headers.
export const useApi = (timeout?: number) => {
  const client = getClient(timeout ?? DEFAULT_TIMEOUT_MS);

  // Keep Authorization on defaults (not only in an interceptor) so raw-fetch
  // helpers that read `api.defaults.headers.common.Authorization` stay in sync
  // when `fa-test-token` is set after mount.
  if (typeof window !== "undefined") {
    const token = window.sessionStorage.getItem("fa-test-token");
    if (token) {
      client.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete client.defaults.headers.common.Authorization;
    }
  }
  return client;
};

// Helper function for streaming fetch requests that need proper authorization headers
export const useStreamingApi = () => {
  const $ = _c(5);
  const api = useApi();
  let t0;
  if ($[0] !== api.defaults.baseURL || $[1] !== api.defaults.headers.common.Authorization) {
    t0 = (url, t1) => {
      const options = t1 === undefined ? {} : t1;
      const headers = {
        Authorization: String(api.defaults.headers.common.Authorization || ""),
        Accept: "application/x-ndjson",
        ...options.headers
      };
      return fetch(`${api.defaults.baseURL}/${url}`, {
        ...options,
        headers,
        credentials: "include"
      });
    };
    $[0] = api.defaults.baseURL;
    $[1] = api.defaults.headers.common.Authorization;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const streamingFetch = t0;
  let t1;
  if ($[3] !== streamingFetch) {
    t1 = {
      streamingFetch
    };
    $[3] = streamingFetch;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};